import React, { createContext, useContext, useState, useEffect } from 'react'

const ConnectivityContext = createContext(null)

export function ConnectivityProvider({ children }) {
  const [connectivityState, setConnectivityState] = useState({
    state: 'UNKNOWN',
    mode: 'AUTO',
    reason: 'initializing',
    lastChangeAt: Date.now(),
  })
  const [isElectron, setIsElectron] = useState(false)

  useEffect(() => {
    const hasElectronAPI = typeof window !== 'undefined' && window.electronAPI
    setIsElectron(!!hasElectronAPI)

    if (!hasElectronAPI) {
      const state = {
        state: 'ONLINE',
        mode: 'AUTO',
        reason: 'web_browser',
        lastChangeAt: Date.now(),
      }
      setConnectivityState(state)
      if (typeof window !== 'undefined') {
        window.__connectivityState = state
      }
      return
    }

    async function initConnectivity() {
      try {
        const state = await window.electronAPI.getConnectivityState()
        setConnectivityState(state)
        if (typeof window !== 'undefined') {
          window.__connectivityState = state
        }
      } catch (error) {
        console.error('[CONNECTIVITY] Failed to get initial state:', error)
      }
    }

    initConnectivity()

    const unsubscribe = window.electronAPI.onConnectivityChange((state) => {
      console.log('[CONNECTIVITY] State changed:', state)
      setConnectivityState(state)
      if (typeof window !== 'undefined') {
        window.__connectivityState = state
      }
    })

    return unsubscribe
  }, [])

  const isOffline = connectivityState.state === 'OFFLINE'
  const isOnline = connectivityState.state === 'ONLINE'
  const isRecovering = connectivityState.state === 'RECOVERING'

  const publishState = (next) => {
    setConnectivityState(next)
    if (typeof window !== 'undefined') {
      window.__connectivityState = next
    }
  }

  const applyBrowserMode = (mode, state, reason) => {
    publishState({
      state,
      mode,
      reason,
      lastChangeAt: Date.now(),
    })
  }

  const forceOffline = async () => {
    if (!isElectron) {
      applyBrowserMode('FORCE_OFFLINE', 'OFFLINE', 'manual_override_offline')
      return
    }
    try {
      const newState = await window.electronAPI.setConnectivityMode('FORCE_OFFLINE')
      publishState(newState)
    } catch (error) {
      console.error('[CONNECTIVITY] Failed to force offline:', error)
    }
  }

  const forceOnline = async () => {
    if (!isElectron) {
      applyBrowserMode('FORCE_ONLINE', 'ONLINE', 'manual_override_online')
      return
    }
    try {
      const newState = await window.electronAPI.setConnectivityMode('FORCE_ONLINE')
      publishState(newState)
    } catch (error) {
      console.error('[CONNECTIVITY] Failed to force online:', error)
    }
  }

  const setAutoMode = async () => {
    if (!isElectron) {
      applyBrowserMode('AUTO', 'ONLINE', 'web_browser')
      return
    }
    try {
      const newState = await window.electronAPI.setConnectivityMode('AUTO')
      publishState(newState)
    } catch (error) {
      console.error('[CONNECTIVITY] Failed to set auto mode:', error)
    }
  }

  const checkConnectivity = async () => {
    if (!isElectron) return { available: true }
    try {
      return await window.electronAPI.checkConnectivity()
    } catch (error) {
      console.error('[CONNECTIVITY] Failed to check connectivity:', error)
      return { available: false, reason: error.message }
    }
  }

  const value = {
    connectivityState,
    isOffline,
    isOnline,
    isRecovering,
    isElectron,
    forceOffline,
    forceOnline,
    setAutoMode,
    checkConnectivity,
  }

  return (
    <ConnectivityContext.Provider value={value}>
      {children}
    </ConnectivityContext.Provider>
  )
}

export function useConnectivity() {
  const context = useContext(ConnectivityContext)
  if (!context) {
    throw new Error('useConnectivity must be used within ConnectivityProvider')
  }
  return context
}
